import React, { useContext, useEffect, useState } from "react";
import BottomDrawer from "../../components/BottomDrawer";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  Tag,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Gig, Venue } from "../../types/MusicFestivalTypes";
import { MapContext } from "../../context/map.context";
import { MapPin } from "../../components/Map";
import {
  getMockMusicFestGigs,
  getMockMusicFestVenues,
  useMusicFestivalGigs,
  useMusicFestivalVenues,
} from "../../data/musicFestival.api";
import { GiGreatPyramid } from "react-icons/gi";
import { convertDateToDateString } from "../../util/dateFormatters";
import Host from "../../components/musicfestival/Host";
import { IoChevronBack } from "react-icons/io5";
import FilterTagList from "../../components/musicfestival/FilterTagList";
import LocationButton from "../../components/LocationButton";

const ViewGig = () => {
  const navigate = useNavigate();

  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLocation = useLocation();

  const { data, loading } = useMusicFestivalVenues();
  const { data: gigData, loading: gigLoading } = useMusicFestivalGigs();

  const { setPins, venues, setVenues, flyTo } = useContext(MapContext);
  const { locationId, gigId } = useParams();

  const [gig, setGig] = useState<Gig>();
  const [filterList, setFilterList] = useState<string[]>([]);
  const [gigDate, setGigDate] = useState<string>("");
  const [searched, setSearched] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);

  useEffect(() => {
    //@ts-ignore
    if (searchParams.size > 0) {
      const searchTerm = searchParams.get("search");
      if (searchTerm !== null) {
        setFilterList([
          ...searchParams.getAll("genre"),
          ...searchParams.getAll("venue"),
          ...searchParams.getAll("accessibility"),
          searchTerm !== null ? searchTerm : "",
        ]);
      } else {
        setFilterList([
          ...searchParams.getAll("genre"),
          ...searchParams.getAll("venue"),
          ...searchParams.getAll("accessibility"),
        ]);
      }
    }
  }, [urlLocation.search]);

  // fetches current gig venues
  const fetchGigAndVenues = () => {
    const listOfVenues: Venue[] =
      data?.getEventLocations != null ? data.getEventLocations : [];
    setVenues(listOfVenues);
    const listOfGigs: Gig[] =
      gigData?.getPublishedEvents != null ? gigData.getPublishedEvents : [];
    let matchedGig = listOfGigs.find((x) => `${x.id}` === gigId);
    if (gigData && gigData.getPublishedEvents) {
      matchedGig = gigData.getPublishedEvents.find(
        (x: Gig) => `${x.id}` === gigId
      );
    }
    const dateString = convertDateToDateString(
      matchedGig ? matchedGig.startDateUTC : ""
    );
    setGigDate(dateString);
    setGig(matchedGig);
  };

  // Moves map to gig event instead of university campus
  useEffect(() => {
    flyTo({
      pitch: 1,
      bearing: 1,
      zoom: 16,
      center: {
        lng: gig?.locations ? gig.locations[0].longitude : -2.803113,
        lat: gig?.locations ? gig.locations[0].latitude : 54.048958,
      },
    });
  }, [gig]);

  useEffect(() => {
    fetchGigAndVenues();
  }, [gigData, data]);

  // renders new map pins if none are present
  useEffect(() => {
    const mapPins = venues.map((venue) => ({
      lng: venue.longitude,
      lat: venue.latitude,
      color: `${venue.id === locationId ? "blue" : "#E9464F"}`,
      onClick: () => navigate(`/musicfestival/${venue.id}`),
    }));
    setPins(mapPins);
  }, [venues]);

  // renders error toast if gig is not found
  useEffect(() => {
    if (!gig && !gigLoading && searched) {
      toast({
        status: "error",
        title: "Gig Not Found",
        description:
          "Please return to gigs at venue page. If problem persists the gig may no longer exist.",
        position: "top",
        isClosable: true,
        duration: 5000,
      });
    }
    if (!gigLoading) {
      setSearched(true);
    }
  }, [gigData, gigLoading, searched]);

  return (
    <>
      {searchParams.get("platform") === "mobile" && (
        <FilterTagList filterList={filterList} />
      )}
      <BottomDrawer ml={"4em"} hideExpand={true} expand={true}>
        {gig && (
          <Flex justifyContent="space-between" m="1em">
            <LocationButton variant="solid">Nearby</LocationButton>
            <IconButton
              aria-label={"Return to music festival home page button"}
              icon={<IoChevronBack />}
              zIndex={999}
              onClick={() => {
                navigate(-1);
              }}
            ></IconButton>
          </Flex>
        )}

        {!gig && gigLoading && (
          <HStack w={"100%"} mb={"1em 0em"} justify={"center"}>
            <Text>Loading Venue</Text>
            <Spinner />
          </HStack>
        )}
        {gig && (
          <VStack align={"start"} p={6} pt={8} mb={4}>
            <HStack w={"100%"}>
              <Heading size={"sm"} color={"#717D96"}>
                {gig?.eventCategory}
              </Heading>
              <Spacer />
              {gig && gig.tags.length > 0 && (
                <Tag bg={"#D4D4D8"} color={"#89898F"}>
                  {gig?.tags?.[0].name}
                </Tag>
              )}
            </HStack>
            <Heading size={"sm"}>{gig?.eventName}</Heading>
            <VStack align={"start"} w={"100%"} spacing={0}>
              <HStack w={"100%"}>
                <Text>{gigDate}</Text>
                <Spacer />
                <Text>
                  {gig?.startTime} - {gig?.endTime}
                </Text>
              </HStack>
              <Text>{gig?.locations[0].title}</Text>
            </VStack>
          </VStack>
        )}
        {!gig && !gigLoading && searched && (
          <VStack align={"start"} p={6}>
            <Alert
              flexDirection={"column"}
              gap={0.5}
              status={"error"}
              borderRadius={"1em"}
            >
              <AlertTitle textAlign={"center"} mb={1.5}>
                Could Not Find This Gig
              </AlertTitle>
              {/* <AlertDescription textAlign={"center"}>Please return to gigs at venue page and try again.</AlertDescription>
            <AlertDescription textAlign={"center"}>If the problem persists this may mean the gig has been removed.</AlertDescription> */}
              <Button
                size={"sm"}
                onClick={() => navigate(`/musicfestival/${locationId}`)}
              >
                Return to gigs at venue page
              </Button>
            </Alert>
          </VStack>
        )}
        {/* todo: view more button that shows host and events details and descriptions */}
        {/* <Button onClick={() => setShow(!show)} size={"sm"} mb={3} >{show ? "Less Info" : "More Info"}</Button>
        {show && <VStack>
          {gig?.hosts.map((host) => {
            return (<Host host={host}/>)
          })}
        </VStack>} */}
      </BottomDrawer>
    </>
  );
};

export default ViewGig;
